/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Commitment status header — the honest summary of a committed membership.
 */
import React from "react";
import { Skeleton } from "@lemonadetraining/web-ui";
import {
  FINALIZING_DETAIL,
  FINALIZING_HEADER,
  GRANDFATHERED_NOTE,
  PAID_UPFRONT_LABEL,
  commitmentHeaderLabel,
  commitmentProgressAriaLabel,
  commitmentProgressText,
  endedLabel,
  formatMonthlyPrice,
  nextChargeLabel,
  paymentsLeftLabel,
  type CommitmentStatus,
  type CommittedStatus,
} from "@lemonadetraining/web-shared/utils/commitmentStatus";

interface MonthlyPrice {
  /** Minor units, as the Stripe price carries it. */
  unitAmount: number;
  currency?: string | null;
}

interface CommitmentStatusHeaderProps {
  status: CommitmentStatus;
  /** Set once the subscription is terminal; `date` is null when Stripe recorded none. */
  ended?: { date: Date | null };
  /** The price being billed NOW — never the post-term roll price. */
  monthlyPrice?: MonthlyPrice | null;
  nextChargeDate?: Date | null;
  /** Pinned by tests; production lets the reader's locale decide. */
  locale?: string;
  /** The gym's zone, so dates read on its calendar; null = the browser's. */
  timeZone?: string | null;
  /** The gym's zone is still being read: hold the date's place rather than guess. */
  datesPending?: boolean;
}

/**
 * Bounds the DOM against a mirror carrying any term length (attach has no
 * upper bound). It engages on terms gyms really sell, such as 24 months in
 * Finland, where the fill is scaled. Shared with mobile.
 */
const MAX_RENDERED_SEGMENTS = 12;

/**
 * `role="img"` with one label makes the segments presentational children, so
 * the bar announces once rather than as a dozen meaningless nodes.
 *
 * Above the cap the filled count is SCALED, not clamped: clamping would draw
 * 4-of-24 as 4-of-12 and read as twice the progress actually served. Only the
 * graphic approximates — the label and the "[N] of [M] months" text beside it
 * state the real figures either way.
 */
const SegmentedMonths: React.FC<{ monthsElapsed: number; commitmentMonths: number }> = ({
  monthsElapsed,
  commitmentMonths,
}) => {
  const segments = Math.min(commitmentMonths, MAX_RENDERED_SEGMENTS);
  // A month served always shows as at least one segment. Rounding alone sends
  // 1-of-600 to an empty bar while the text beside it says a month has passed,
  // and an empty bar reads as "nothing has happened yet".
  const scaled = Math.round((monthsElapsed / commitmentMonths) * segments);
  const filled = Math.min(segments, monthsElapsed >= 1 ? Math.max(1, scaled) : scaled);

  return (
    <div
      role="img"
      aria-label={commitmentProgressAriaLabel(monthsElapsed, commitmentMonths)}
      data-testid="commitment-progress-bar"
      className="flex flex-1 items-center gap-0.5 min-w-[120px] max-w-[220px]"
    >
      {Array.from({ length: segments }, (_, index) => (
        <span
          key={index}
          data-testid="commitment-progress-segment"
          data-filled={index < filled}
          className={`h-2 flex-1 rounded-sm border border-muted-foreground ${
            index < filled ? "bg-primary" : "bg-muted"
          }`}
        />
      ))}
    </div>
  );
};

const PaymentLine: React.FC<{
  status: CommittedStatus;
  monthlyPrice?: MonthlyPrice | null;
  nextChargeDate?: Date | null;
  locale?: string;
}> = ({ status, monthlyPrice, nextChargeDate, locale }) => {
  if (status.paymentMode === "upfront") {
    return (
      <span className="text-xs text-muted-foreground" data-testid="commitment-payment-line">
        {PAID_UPFRONT_LABEL}
      </span>
    );
  }

  // A missing part is dropped, never guessed: an invented figure on a billing
  // summary is worse than a shorter line.
  const parts: string[] = [];
  if (monthlyPrice && Number.isFinite(monthlyPrice.unitAmount)) {
    parts.push(formatMonthlyPrice(monthlyPrice.unitAmount, monthlyPrice.currency));
  }
  if (status.paymentsLeft !== null) {
    parts.push(paymentsLeftLabel(status.paymentsLeft));
  }
  if (nextChargeDate) {
    parts.push(nextChargeLabel(nextChargeDate, locale));
  }
  if (parts.length === 0) return null;

  return (
    <span className="text-xs text-muted-foreground" data-testid="commitment-payment-line">
      {parts.join(" · ")}
    </span>
  );
};

const DatePlaceholder: React.FC = () => (
  <Skeleton className="h-4 w-40" data-testid="commitment-until-loading" />
);

/**
 * The forwarded ref lands on the committed summary, which is the stable anchor
 * a dismissed notice hands focus back to — without it the activated button
 * unmounts and focus falls to `<body>`, restarting the reader's next Tab at
 * the top of the page.
 */
const CommitmentStatusHeader = React.forwardRef<HTMLDivElement, CommitmentStatusHeaderProps>(
  ({ status, ended, monthlyPrice, nextChargeDate, locale, timeZone, datesPending }, ref) => {
    if (ended) {
      if (datesPending) return <DatePlaceholder />;
      return (
        <span className="text-xs font-medium text-foreground" data-testid="commitment-ended">
          {endedLabel(ended.date, timeZone)}
        </span>
      );
    }

    if (status.kind === "none") {
      return (
        <span className="text-xs text-muted-foreground" data-testid="commitment-grandfathered-note">
          {GRANDFATHERED_NOTE}
        </span>
      );
    }

    if (status.kind === "finalizing") {
      return (
        <div className="flex flex-col gap-0.5" data-testid="commitment-finalizing">
          <span className="text-xs font-medium text-foreground">{FINALIZING_HEADER}</span>
          <span className="text-xs text-muted-foreground">{FINALIZING_DETAIL}</span>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        tabIndex={-1}
        className="flex flex-col gap-1 outline-none"
        data-testid="commitment-status-header"
      >
        {datesPending ? (
          <DatePlaceholder />
        ) : (
          <span className="text-xs font-medium text-foreground" data-testid="commitment-until">
            {commitmentHeaderLabel(status.commitmentEndDate, timeZone)}
          </span>
        )}

        <div className="flex items-center gap-2">
          <SegmentedMonths
            monthsElapsed={status.monthsElapsed}
            commitmentMonths={status.commitmentMonths}
          />
          <span className="text-xs text-muted-foreground" data-testid="commitment-progress-text">
            {commitmentProgressText(status.monthsElapsed, status.commitmentMonths)}
          </span>
        </div>

        <PaymentLine
          status={status}
          monthlyPrice={monthlyPrice}
          nextChargeDate={nextChargeDate}
          locale={locale}
        />
      </div>
    );
  }
);

CommitmentStatusHeader.displayName = "CommitmentStatusHeader";

export default CommitmentStatusHeader;
